import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cn from 'classnames'
import { omit } from 'lodash'
import moment from 'moment'
import React from 'react'
import { i18n } from '../../domain/index/index'
import RequiresAction from '../../domain/weeklyInfo/RequiresAction'
import { Mutation, Query } from '../../react-apollo/Components'
import Loader from '../loader/Loader'
import { fetchHesburgerServicePromptness, saveHesburgerServicePromptness } from './queries'
import css from './styles.css'

const SERVICE_PROMPTNESS_THRESHOLD = '00:07:00'

type HesburgerServicePromptness = {
  date: string
  cashier: string
  driveIn: string
  selfServiceCashier: string
  app: string
  foodora: string
  all: string
  isActionRequired: boolean
}

type HesburgerServicePromptnessTableProps = {
  stationId: string
  date: string
  isMonthlyInfo?: boolean
}

const fields = ['cashier', 'driveIn', 'selfServiceCashier', 'app', 'foodora', 'all']

export const HesburgerServicePromptnessTable = ({
  stationId,
  date,
  isMonthlyInfo,
}: HesburgerServicePromptnessTableProps) => (
  <Query<{ hesburgerServicePromptness: HesburgerServicePromptness }, { stationId: string; date: string }>
    query={fetchHesburgerServicePromptness}
    variables={{
      stationId,
      date,
    }}
  >
    {({ data, error, loading, refetch }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.salesAndHours}>
          <Typography variant={'h6'} gutterBottom>
            {isMonthlyInfo
              ? i18n.t('monthlyInfo.title.hesburgerServicePromptness', {
                  month: moment(date).format('MMMM'),
                })
              : i18n.t('weeklyInfo.title.hesburgerServicePromptness', {
                  month: moment(date).format('MMMM'),
                })}
          </Typography>
          <Paper>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {fields.map((field) => (
                    <TableCell key={field} className={cn(css.tableCell, css.cellCenter)}>
                      {i18n.t(`weeklyInfo.heading.${field}`)}
                    </TableCell>
                  ))}
                  <TableCell className={cn(css.tableCell, css.cellCenter)}>
                    {i18n.t('weeklyInfo.heading.isActionRequired')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <HesburgerServicePromptnessRow
                  stationId={stationId}
                  date={date}
                  data={data?.hesburgerServicePromptness as HesburgerServicePromptness}
                  refetch={refetch}
                />
              </TableBody>
            </Table>
          </Paper>
        </div>
      </Loader>
    )}
  </Query>
)

interface HesburgerServicePromptnessRowProps {
  stationId: string
  date: string
  data?: HesburgerServicePromptness
  refetch: (...args: Array<any>) => any
}

const HesburgerServicePromptnessRow = ({ stationId, date, data, refetch }: HesburgerServicePromptnessRowProps) => {
  const onActionRequiredChange = async (onMutate: (...args: Array<any>) => any, required: boolean) => {
    const variables = {
      stationId,
      date,
      data: {
        ...omit(data, ['__typename']),
        isActionRequired: required,
      },
    }
    await onMutate({
      variables,
    })
    await refetch()
  }

  return (
    <TableRow>
      {fields.map((field) => {
        const value = data?.[field]
        const colorStyle = value && (value > SERVICE_PROMPTNESS_THRESHOLD ? css.negativeCell : css.positiveCell)
        return (
          <TableCell key={field} className={cn(css.tableCell, css.cellCenter, colorStyle)}>
            {data ? (value ?? '-') : null}
          </TableCell>
        )
      })}
      <TableCell className={cn(css.tableCell, css.cellCenter, css.actionCell)}>
        {data && (
          <Mutation mutation={saveHesburgerServicePromptness}>
            {(onMutate) => (
              <RequiresAction
                stationId={stationId}
                date={date}
                subject='Hesburger'
                isActionRequired={data?.isActionRequired}
                callback={(required: boolean) => onActionRequiredChange(onMutate, required)}
              />
            )}
          </Mutation>
        )}
      </TableCell>
    </TableRow>
  )
}
